<template>
    <div>
    <base-dialog :loading="loading" :value="dialog" :width="480" :title="`${editMode ? $t('common.edit') : $t('common.add')} ${$t('common.file')}`"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="file-body">
            <kimsa-text-field no-elevation bolder-label dense :label="$t('common.name')" :value="name"
                @change="name = $event" force-value />
            <div class="field-group">
                <div class="field-label">{{ $t('common.file') }}</div>
                <v-file-input class="file-field" :disabled="editMode" solo flat dense type="file"
                    accept=".xlsx,.xls,.csv,.doc,.docx" @change="file = $event" :placeholder="placeholderFile"
                    hide-details color="secondary" prepend-icon="" prepend-inner-icon="mdi-paperclip" />
            </div>
        </div>

        <template v-slot:footer>
            <v-btn v-if="editMode" block depressed x-large color="error" class="footer-btn" :loading="loadingDelete"
                @click="deleteFile">
                {{ $t('common.delete') }}
            </v-btn>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" :disabled="disabled"
                @click="save" :loading="loading">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>

    <kimsa-confirm ref="confirmDelete" :title="$t('common.delete_file')" :content="$t('common.confirm_delete_file')"
        :loading="loadingDelete" :persistent="false" :close-on-confirm="false" :alert-mode="true"
        @confirm="onConfirmDelete" />
    </div>
</template>

<script>
import { storageRef } from '@/utils/Firebase'
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { db, Timestamp } from "@/utils/Firebase";
import BaseDialog from "@/components/common/BaseDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";

export default {
    name: "FilesStudentDialog",
    components: { KimsaTextField, BaseDialog, KimsaConfirm },
    props: {
        studentId: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingDelete: false,
            editMode: false,
            name: '',
            file: null,
            fileName: '',
            studentFile: null,
        }
    },
    computed: {
        disabled(vm = this) {
            return !vm.name || !vm.file
        },
        placeholderFile(vm = this) {
            return vm.editMode ? vm.fileName : vm.$t('common.select')
        },
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.editMode = false
            vm.name = ''
            vm.file = null
            vm.fileName = ''
            vm.studentFile = null
        },
        setDefaults(file) {
            const vm = this
            vm.name = file.name
            vm.fileName = file.fileName
            vm.file = file.url
            vm.studentFile = file
            vm.editMode = true
            vm.show()
        },
        async save() {
            const vm = this
            if (vm.loadingDelete) return;
            const { editMode, name, studentFile } = vm
            await vm.withLoading(async () => {
                if (editMode) {
                    await db.collection('studentFiles').doc(studentFile.id).update({
                        name,
                    })
                } else {
                    const fileUploaded = await vm.uploadFile()

                    await db.collection('studentFiles').add({
                        studentId: vm.studentId,
                        coachId: vm.coachId,
                        name,
                        fileName: fileUploaded.name,
                        filePath: fileUploaded.filePath,
                        url: fileUploaded.url,
                        createdAt: Timestamp.now()
                    })
                }
                vm.$emit('saved')

                vm.toast(vm.$t('common.file_saved'))
                vm.hide()
            })
        },
        deleteFile() {
            const vm = this
            if (vm.loading) return;
            vm.$refs.confirmDelete.show()
        },
        async onConfirmDelete() {
            const vm = this
            const studentFile = vm.studentFile
            await vm.withLoading(async () => {
                if (studentFile.filePath) {
                    const fileRef = storageRef.child(studentFile.filePath)
                    await fileRef.delete()
                }

                await db.collection('studentFiles').doc(studentFile.id).delete()
                vm.$emit('saved')

                vm.$refs.confirmDelete?.hide()
                vm.toast(vm.$t('common.file_deleted'))
                vm.hide()
            }, 'loadingDelete')
        },
        async uploadFile() {
            const vm = this
            const file = vm.file
            if (!file) return
            const savePath = `/${vm.coachId}/student/${vm.studentId}/files/${vm.$moment().unix()}-${file.name}`
            let uploadTask = await storageRef.child(savePath).put(file)
            return {
                url: await uploadTask.ref.getDownloadURL(),
                filePath: savePath,
                name: file.name,
            }
        },
    },
}
</script>

<style scoped>
.file-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.field-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtSecondary-base);
}

.file-field >>> .v-input__slot {
    background: var(--v-field-base) !important;
    border: 1px solid var(--v-outline-base) !important;
    border-radius: 10px !important;
    min-height: 46px;
    padding: 0 12px !important;
    box-shadow: none !important;
    transition: border-color 0.15s;
}

.file-field.v-input--is-focused >>> .v-input__slot {
    border-color: var(--v-secondary-base) !important;
}

.file-field >>> .v-file-input__text {
    color: var(--v-txtPrimary-base);
    font-size: 14px;
}

.file-field >>> input::placeholder {
    color: var(--v-txtMuted-base);
}

.file-field >>> .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>
